import { useEffect, useState } from 'react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { FormField, Textarea } from '../../components/ui/Field';

/** A pending request-changes / reject decision awaiting its required comment. */
export interface DecisionCommentTarget {
  /** Modal heading, e.g. "Request changes". */
  title: string;
  /** Confirm button label, e.g. "Request changes" / "Reject". */
  confirmLabel: string;
  /** Red confirm button (reject). */
  danger?: boolean;
  /** Called with the trimmed, non-empty comment when confirmed. */
  onConfirm: (comment: string) => void;
}

/**
 * Comment modal for request-changes / reject decisions. A comment is REQUIRED (the server rejects
 * these decisions without one) and is recorded on the approval transition — i.e. it shows up in
 * the "Approval history" section. Replaces the old window.prompt.
 */
export function DecisionCommentModal({
  target,
  onClose,
}: {
  target: DecisionCommentTarget | null;
  onClose: () => void;
}) {
  const [comment, setComment] = useState('');
  // Reset the field each time a new decision opens the modal.
  useEffect(() => {
    setComment('');
  }, [target]);

  const trimmed = comment.trim();
  const canConfirm = trimmed.length > 0;

  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      title={target?.title ?? ''}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={target?.danger ? 'danger' : 'primary'}
            disabled={!canConfirm}
            onClick={() => {
              if (canConfirm && target) target.onConfirm(trimmed);
            }}
          >
            {target?.confirmLabel ?? 'Confirm'}
          </Button>
        </>
      }
    >
      <FormField label="Comment (required)" htmlFor="decision-comment">
        <Textarea
          id="decision-comment"
          rows={4}
          autoFocus
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Explain what needs to change…"
        />
      </FormField>
      <p className="mt-2 text-xs text-ink-faint">
        This comment is recorded in the approval history.
      </p>
    </Modal>
  );
}
